import React, { type CSSProperties, type ComponentType, type ReactNode, memo } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../common/constants.tsx';
import type { ColumnType } from '../../../../common/types.tsx';
import ErrorCell from '../../../../common/ui/error-cell/index.tsx';
import {
	TableCell,
	type Props as TableCellProps,
} from '../../../../common/ui/table-cell/index.tsx';
import { MultipleCellElements } from './multiple-cell-elements/index.tsx';

const DefaultFallbackComponent = () => <></>;

type Props = {
	/**
	 * Type of the issue field.
	 */
	type: ColumnType;
	/**
	 * Element to render before the cell content of an issue field.
	 */
	beforeElement?: ReactNode;
	/**
	 * Table cell element to render for an issue field.
	 */
	cellElement: ReactNode;
	/**
	 * Optional border styles to apply to the `td` element. If not provided, no borders will be rendered.
	 */
	border?: TableCellProps['border'];
	/**
	 * Optional spacing styles to apply to the `td` element. If not provided, default spacing will apply.
	 */
	spacing?: TableCellProps['spacing'];
	/**
	 * Flag to enable min-height containers around each individual cell element as a stop-gap solution to fix layout
	 * issues with duplicate inline editable fields (which have built-in `min-height: 100%`).
	 * TODO JSC-1783 Handle duplicate field layouts within single row height
	 */
	multipleFieldLayoutAdjustment?: boolean;
	/**
	 * Component to display if the `cellElement` is undefined. This will default to an empty table cell if not provided.
	 */
	FallbackComponent?: ComponentType;
	style?: CSSProperties;
	formatting?: TableCellProps['formatting'];
	isColumnResizing?: boolean;
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	hasRightShadow?: boolean;
	fieldId?: string;
	onFocusHandler?: () => void;
	onBlurHandler?: () => void;
};

const IssueRowCellErrorFallback = ({
	type,
	border,
	error,
}: {
	type: ColumnType;
	columnWidth?: number;
	isFlexWidth?: boolean;
	border?: TableCellProps['border'];
	error: Error;
}) => (
	<TableCell border={border}>
		<ErrorCell
			cellType={type}
			reason={fg('empanada_error_cell_logging') ? error : 'Unhandled error'}
		/>
	</TableCell>
);
const IssueRowCell = ({
	type,
	beforeElement,
	cellElement,
	border,
	spacing,
	multipleFieldLayoutAdjustment,
	FallbackComponent = DefaultFallbackComponent,
	formatting,
	style,
	isColumnResizing,
	isColumnSticky,
	stickyOffsets,
	hasRightShadow,
	fieldId,
	onFocusHandler,
	onBlurHandler,
}: Props) => (
	<ContextualAnalyticsData attributes={{ fieldType: type }}>
		<JSErrorBoundary
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			id="issueRowCellUnhandled"
			sendToPrivacyUnsafeSplunk
			fallback={({ error }) => (
				<IssueRowCellErrorFallback type={type} border={border} error={error} />
			)}
		>
			<TableCell
				spacing={spacing}
				border={border}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
				style={style}
				{...(fg('issue_table_frozen_columns') && {
					isColumnSticky,
					stickyOffsets,
					isColumnResizing,
					hasRightShadow,
				})}
				formatting={formatting}
				fieldId={fieldId}
				{...(onFocusHandler && fg('jira_list_merged_cell_action_buttons_visibility')
					? { onFocusHandler }
					: {})}
				{...(onBlurHandler && fg('jira_list_merged_cell_action_buttons_visibility')
					? { onBlurHandler }
					: {})}
			>
				<>
					{beforeElement}
					{Array.isArray(cellElement) && cellElement.length > 1 ? (
						<MultipleCellElements
							cellElements={cellElement}
							type={type}
							multipleFieldLayoutAdjustment={multipleFieldLayoutAdjustment}
						/>
					) : (
						(cellElement ?? <FallbackComponent />)
					)}
				</>
			</TableCell>
		</JSErrorBoundary>
	</ContextualAnalyticsData>
);

export default memo<Props>(IssueRowCell);
